import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "react-bootstrap/Breadcrumb"; //Breadcrumb
import Accordion from "react-bootstrap/Accordion"; //Accordion
import AccordionData from "../data/AccordionData"; //AccordionData
import axios from "../api/axios";

import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/styles.min.css";

import "./ProductDetail.css";

import StarPurple500OutlinedIcon from "@mui/icons-material/StarPurple500Outlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FavoriteIcon from "@mui/icons-material/Favorite";

import Facebook from "../assets/images/Facebook.png";
import Instagram from "../assets/images/Instagram.png";
import Whatsapp from "../assets/images/Whatsapp.png";
import Call from "../assets/images/Call.png";

import colorpillows from "../assets/images/carpet.png";

import CartSidebar from "../Components/CartSidebar";

export default function DetailInfo() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mainImg, setMainImg] = useState("");
  const [counter, setCounter] = useState(1);
  const [cartOpen, setCartOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});

  // Dynamic state from variant price api
  const [currentPrice, setCurrentPrice] = useState("");
  const [inStock, setInStock] = useState(true);
  const [isInWishlist, setIsInWishlist] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`/products/${slug}/0`)
      .then(response => {
        if (response.data && response.data.data && response.data.data.length > 0) {
          const productData = response.data.data[0];
          setProduct(productData);
          if (productData.photos && productData.photos.length > 0) {
            setMainImg(productData.photos[0].path);
          } else {
            setMainImg(productData.thumbnail_image);
          }
        }
        setLoading(false);
      })
      .catch(error => {
        console.error("Error fetching product details:", error);
        setLoading(false);
      });
  }, [slug]);

  // Handle initializing defaults once the product is fetched
  useEffect(() => {
    if (product) {
      setCurrentPrice(product.main_price);
      setInStock(product.current_stock > 0);

      // Auto-select first color if available
      if (product.colors && product.colors.length > 0) {
        setSelectedColor(product.colors[0]);
      }

      // Auto-select first option for each choice
      if (product.choice_options && product.choice_options.length > 0) {
        const initialOptions = {};
        product.choice_options.forEach(opt => {
          if (opt.options && opt.options.length > 0) {
            initialOptions[opt.name] = opt.options[0];
          }
        });
        setSelectedOptions(initialOptions);
      }
    }
  }, [product]);

  const [reviews, setReviews] = useState([]);

  // Fetch reviews when product details are loaded
  useEffect(() => {
    if (product) {
      axios.get(`/reviews/product/${product.id}`)
        .then(res => {
          if (res.data && res.data.data) {
            setReviews(res.data.data);
          }
        })
        .catch(err => {
          console.error("Error fetching reviews:", err);
        });
    }
  }, [product]);

  const [socialLinks, setSocialLinks] = useState({
    facebook: "",
    instagram: "",
    phone: ""
  });

  // Fetch business settings for social links and call/phone info
  useEffect(() => {
    const keys = ["contact_phone", "facebook_link", "instagram_link"].join(",");
    axios.post("/business-settings", { keys })
      .then(res => {
        if (res.data && Array.isArray(res.data)) {
          const findVal = (key) => {
            // First search for English translation/localized version
            let setting = res.data.find(s => s.type === key && s.lang === "en");
            // If not found, look for any non-empty version
            if (!setting) {
              setting = res.data.find(s => s.type === key && s.value !== null && s.value !== "");
            }
            // Fallback to the first match
            if (!setting) {
              setting = res.data.find(s => s.type === key);
            }
            return setting?.value;
          };
          setSocialLinks({
            facebook: findVal("facebook_link") || "",
            instagram: findVal("instagram_link") || "",
            phone: findVal("contact_phone") || ""
          });
        }
      })
      .catch(err => {
        console.error("Error fetching business settings in DetailInfo:", err);
      });
  }, []);

  // Fetch variant price and details when selection changes
  useEffect(() => {
    if (!product) return;

    // Guard: if product has colors but none selected yet, wait
    if (product.colors && product.colors.length > 0 && !selectedColor) return;

    // Guard: if product has choice options but not all are selected yet, wait
    if (product.choice_options && product.choice_options.length > 0) {
      const allSelected = product.choice_options.every(opt => selectedOptions[opt.name]);
      if (!allSelected) return;
    }

    const fetchVariantPrice = async () => {
      let colorParam = selectedColor ? selectedColor.replace("#", "") : "";
      let variantsParam = product.choice_options && product.choice_options.length > 0
        ? product.choice_options.map(opt => selectedOptions[opt.name]).filter(Boolean).join(",")
        : "";

      try {
        const priceRes = await axios.post("/products/variant/price", {
          slug: slug,
          quantity: 1,
          color: colorParam,
          variants: variantsParam
        });

        if (priceRes.data && priceRes.data.result && priceRes.data.data) {
          const data = priceRes.data.data;
          setCurrentPrice(data.price);
          setInStock(data.in_stock === 1 || data.stock > 0);
          
          if (data.image) {
            setMainImg(data.image);
          }
        }
      } catch (err) {
        console.error("Error fetching variant price:", err);
      }
    };

    fetchVariantPrice();
  }, [selectedColor, selectedOptions, product]);

  // Check wishlist status on mount/product load
  useEffect(() => {
    const checkWishlist = () => {
      const token = localStorage.getItem("access_token");
      if (token && product) {
        try {
          const stored = localStorage.getItem("wishlist_slugs");
          if (stored) {
            const slugs = JSON.parse(stored);
            setIsInWishlist(slugs.includes(slug));
          } else {
            setIsInWishlist(false);
          }
        } catch (e) {
          setIsInWishlist(false);
        }
      } else {
        setIsInWishlist(false);
      }
    };
    checkWishlist();

    window.addEventListener("wishlist-slugs-updated", checkWishlist);
    window.addEventListener("wishlist-updated", checkWishlist);
    return () => {
      window.removeEventListener("wishlist-slugs-updated", checkWishlist);
      window.removeEventListener("wishlist-updated", checkWishlist);
    };
  }, [product, slug]);

  const handleToggleWishlist = async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      alert("Please login first to manage your wishlist.");
      const loginPath = "/login";
      window.location.href = loginPath;
      return;
    }

    try {
      let storedSlugs = [];
      try {
        const stored = localStorage.getItem("wishlist_slugs");
        if (stored) storedSlugs = JSON.parse(stored);
      } catch (err) {}

      if (isInWishlist) {
        const res = await axios.get(`/wishlists-remove-product/${slug}`);
        if (res.status === 200) {
          setIsInWishlist(false);
          const updated = storedSlugs.filter(s => s !== slug);
          localStorage.setItem("wishlist_slugs", JSON.stringify(updated));
          window.dispatchEvent(new CustomEvent("wishlist-updated"));
          window.dispatchEvent(new CustomEvent("wishlist-slugs-updated"));
        }
      } else {
        const res = await axios.get(`/wishlists-add-product/${slug}`);
        if (res.status === 200) {
          setIsInWishlist(true);
          if (!storedSlugs.includes(slug)) {
            storedSlugs.push(slug);
          }
          localStorage.setItem("wishlist_slugs", JSON.stringify(storedSlugs));
          window.dispatchEvent(new CustomEvent("wishlist-updated"));
          window.dispatchEvent(new CustomEvent("wishlist-slugs-updated"));
        }
      }
    } catch (err) {
      console.error("Error updating wishlist:", err);
    }
  };

  const handleAddToCart = async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      alert("Please login first to add products to the cart.");
      const loginPath = "/login";
      window.location.href = loginPath;
      return;
    }

    const userData = localStorage.getItem("user");
    let userId = null;
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        userId = parsed.id;
      } catch (e) {
        console.error("Error parsing user data", e);
      }
    }

    if (product.colors && product.colors.length > 0 && !selectedColor) {
      alert("Please select a color.");
      return;
    }

    if (product.choice_options && product.choice_options.length > 0) {
      for (let option of product.choice_options) {
        if (!selectedOptions[option.name]) {
          alert(`Please select a ${option.title}.`);
          return;
        }
      }
    }

    let colorParam = selectedColor ? selectedColor.replace("#", "") : "";
    let variantsParam = product.choice_options && product.choice_options.length > 0
      ? product.choice_options.map(opt => selectedOptions[opt.name]).filter(Boolean).join(",")
      : "";

    let variantName = "";
    try {
      const priceRes = await axios.post("/products/variant/price", {
        slug: slug,
        quantity: counter,
        color: colorParam,
        variants: variantsParam
      });

      if (priceRes.data && priceRes.data.result && priceRes.data.data) {
        variantName = priceRes.data.data.variant;
      }
    } catch (err) {
      console.error("Error fetching variant name:", err);
    }

    try {
      const response = await axios.post("/carts/add", {
        user_id: userId,
        id: product.id,
        quantity: counter,
        variant: variantName
      });

      if (response.data && response.data.result) {
        window.dispatchEvent(new CustomEvent("cart-updated"));
        setCartOpen(true);
      } else {
        alert(response.data.message || "Failed to add product to cart.");
      }
    } catch (error) {
      console.error("Error adding product to cart:", error);
      alert(error.response?.data?.message || "Something went wrong. Please try again.");
    }
  };

  const inc = () => {
    setCounter(counter + 1);
  };

  const dec = () => {
    if (counter > 1) {
      setCounter(counter - 1);
    }
  };

  if (loading) return <div className="container py-5 text-center">Loading...</div>;
  if (!product) return <div className="container py-5 text-center">Product not found.</div>;

  return (
    <div className="detail-outer">
      <div className="container-fluid">
        <div className="breadcrumb_title detail_page_breadcrumb">
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item active>
              {product.name}
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>

        <div className="inner-detail">
          <div className="row">
            <div className="col-lg-6 col-md-6 col-12">
              <div className="img-section-detail">
                <div className="gallery">
                  {/* left side */}
                  <div className="thumbnails">
                    {product.photos && product.photos.map((img, index) => (
                      <img
                        key={index}
                        src={img.path}
                        alt=""
                        onClick={() => setMainImg(img.path)}
                        className={`thumb ${mainImg === img.path ? "active" : ""}`}
                      />
                    ))}
                  </div>

                  {/* right side */}
                  
                  <div className="main-image">
                    <InnerImageZoom
                    src={mainImg}
                    zoomSrc={mainImg}
                  />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6 col-md-6 col-12">
              <div className="text-section-detail">
                <h1 className="detail-heading">
                  {product.name}
                </h1>
                <div className="star_review">
                  <div className="stars">
                    {[...Array(5)].map((_, i) => (
                      <StarPurple500OutlinedIcon key={i} />
                    ))}
                  </div>

                  <p className="starpera">({product.rating} Rating)</p>
                </div>

                {product.sku && (
                  <p className="code-in-detail">SKU: {product.sku}</p>
                )}

                <div className="stock-status-wrapper mt-2 mb-3 d-flex align-items-center gap-3">
                  <span className={`badge ${inStock ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: '0.9rem', padding: '6px 12px' }}>
                    {inStock ? "In Stock" : "Out Of Stock"}
                  </span>
                  {product.est_shipping_time > 0 && (
                    <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                      Delivery in {product.est_shipping_time} {product.est_shipping_time === 1 ? 'day' : 'days'}
                    </span>
                  )}
                </div>

                <div className="price-section">
                  {product.has_discount && (
                    <span className="old-price">{product.stroked_price}</span>
                  )}
                  <span className="new-price">{currentPrice || product.main_price}</span>
                  {product.discount !== "-0%" && product.discount && (
                    <span className="discount-badge">You saved {product.discount}</span>
                  )}
                  <span className="tax-text">(MRP incl. of all taxes)</span>
                </div>

                {product.colors && product.colors.length > 0 && (
                  <div className="color-section">
                    <p>Color:</p>
                    <div className="color-box">
                      {product.colors.map((color, index) => (
                        <div 
                          key={index} 
                          className={`main-color-box ${selectedColor === color ? 'active' : ''}`}
                          style={{ 
                            backgroundColor: color, 
                            width: '30px', 
                            height: '30px', 
                            cursor: 'pointer',
                            border: selectedColor === color ? '2px solid #000' : '1px solid #ccc',
                            borderRadius: '50%'
                          }}
                          onClick={() => setSelectedColor(color)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {product.choice_options && product.choice_options.map((option, index) => (
                  <div className="size-section" key={index}>
                    <p className="size-title">{option.title}:</p>
                    <div className="size-options">
                      {option.options.map((val, i) => (
                        <div 
                          key={i} 
                          className={`size-box ${selectedOptions[option.name] === val ? 'active' : ''}`}
                          onClick={() => setSelectedOptions({ ...selectedOptions, [option.name]: val })}
                        >
                          <span>{val}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="counter_add_cart_section">
                <div className="counter">
                  <button onClick={dec}>-</button>
                  <p className="number">{counter}</p>
                  <button onClick={inc}>+</button>
                </div>

                <div className="add_cart_btn">
                  <button 
                    onClick={handleAddToCart}
                    disabled={!inStock}
                    style={{ backgroundColor: !inStock ? '#cccccc' : '' }}
                  >
                    {inStock ? "Add To Cart" : "Out Of Stock"}
                  </button>
                </div>

                <CartSidebar cartOpen={cartOpen} setCartOpen={setCartOpen} />

                <div className="wishlist_btn">
                  <button onClick={handleToggleWishlist} style={{ color: isInWishlist ? '#ff3f6c' : '' }}>
                    {isInWishlist ? <FavoriteIcon /> : <FavoriteBorderOutlinedIcon />}
                  </button>
                </div>
              </div>

              <div className="color-section">
                <p>Share :</p>
                <div className="social_share">
                  <div className="social_icons">
                    <ul>
                      <li>
                        <a href={socialLinks.facebook || `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer">
                          <img src={Facebook} alt="Facebook" className="img-fluid"/> 
                        </a>
                      </li>
                      <li>
                        <a href={socialLinks.instagram || "https://www.instagram.com"} target="_blank" rel="noopener noreferrer">
                          <img src={Instagram} alt="Instagram" className="img-fluid"/>
                        </a>
                      </li>
                      <li>
                        <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(product.name + " - " + window.location.href)}`} target="_blank" rel="noopener noreferrer">
                          <img src={Whatsapp} alt="WhatsApp" className="img-fluid"/>
                        </a>
                      </li>
                      <li>
                        <a href={`tel:${socialLinks.phone || "+918739990555"}`}>
                          <img src={Call} alt="Call" className="img-fluid"/>
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="detail_accord">
                <Accordion defaultActiveKey="description">
                  {/* Description */}
                  {product.description && (
                    <Accordion.Item eventKey="description">
                      <Accordion.Header>Description</Accordion.Header>
                      <Accordion.Body dangerouslySetInnerHTML={{ __html: product.description }} />
                    </Accordion.Item>
                  )}

                  {/* Product Details */}
                  {(product.sku || (product.brand && product.brand.name) || product.unit) && (
                    <Accordion.Item eventKey="details">
                      <Accordion.Header>Product Details</Accordion.Header>
                      <Accordion.Body>
                        <table className="table table-bordered spec-table mb-0">
                          <tbody>
                            {product.sku && (
                              <tr>
                                <th style={{ width: '30%', fontWeight: '600' }}>SKU</th>
                                <td>{product.sku}</td>
                              </tr>
                            )}
                            {product.brand && product.brand.name && (
                              <tr>
                                <th style={{ fontWeight: '600' }}>Brand</th>
                                <td>{product.brand.name}</td>
                              </tr>
                            )}
                            {product.unit && (
                              <tr>
                                <th style={{ fontWeight: '600' }}>Unit</th>
                                <td>{product.unit}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {/* Shipping & Delivery */}
                  {product.shipping_text && (
                    <Accordion.Item eventKey="shipping">
                      <Accordion.Header>Shipping & Delivery</Accordion.Header>
                      <Accordion.Body style={{ whiteSpace: "pre-line" }}>
                        {product.shipping_text}
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {/* Exchange & Return */}
                  {product.exchange_return_text && (
                    <Accordion.Item eventKey="exchange">
                      <Accordion.Header>Exchange & Return</Accordion.Header>
                      <Accordion.Body style={{ whiteSpace: "pre-line" }}>
                        {product.exchange_return_text}
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {/* Care (displays warranty_text) */}
                  {product.warranty_text && (
                    <Accordion.Item eventKey="care">
                      <Accordion.Header>Care</Accordion.Header>
                      <Accordion.Body style={{ whiteSpace: "pre-line" }}>
                        {product.warranty_text}
                      </Accordion.Body>
                    </Accordion.Item>
                  )}

                  {/* Additional Info (displays reviews) */}
                  <Accordion.Item eventKey="additional_info">
                    <Accordion.Header>Additional Info (Reviews)</Accordion.Header>
                    <Accordion.Body>
                      {reviews && reviews.length > 0 ? (
                        <div className="reviews-section">
                          {reviews.map((rev, idx) => (
                            <div key={idx} className="review-item mb-3 pb-3 border-bottom">
                              <div className="d-flex align-items-center mb-1">
                                <div className="review-avatar mr-2" style={{ 
                                  width: '36px', 
                                  height: '36px', 
                                  borderRadius: '50%', 
                                  backgroundColor: '#eee', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center', 
                                  overflow: 'hidden' 
                                }}>
                                  {rev.avatar ? (
                                    <img src={rev.avatar} alt={rev.user_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  ) : (
                                    <span className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                                      {rev.user_name ? rev.user_name.charAt(0).toUpperCase() : '?'}
                                    </span>
                                  )}
                                </div>
                                <div>
                                  <h6 className="mb-0 fs-14 fw-700" style={{ fontSize: '0.9rem' }}>{rev.user_name}</h6>
                                  <span className="text-muted" style={{ fontSize: '0.75rem' }}>{rev.time}</span>
                                </div>
                                <div className="ml-auto d-flex align-items-center text-warning" style={{ marginLeft: 'auto' }}>
                                  {[...Array(5)].map((_, i) => (
                                    <StarPurple500OutlinedIcon 
                                      key={i} 
                                      style={{ fontSize: '0.9rem', color: i < rev.rating ? '#ffc107' : '#e4e5eb' }} 
                                    />
                                  ))}
                                </div>
                              </div>
                              <p className="mb-0 mt-2 fs-13 text-secondary" style={{ paddingLeft: '44px', fontSize: '0.85rem' }}>
                                {rev.comment}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted mb-0">No reviews yet for this product.</p>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
